import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import queryKeys from "shared/utils/constants/queryKeys";
import { getTheme } from "services/publicLiveScores";
import { useLocation } from "react-router-dom";
import CardLaneV2 from "views/components/cardByLane/cardByLaneV2";
import CardCompacted from "views/components/cardByLane/cardCompacted";
import styles from "./styles.module.scss";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { v4 as uuidv4 } from "uuid";
import PBAScoreCompacted from "views/components/pbaScore/compacted";
import PBAScoreFull from "views/components/pbaScore/full";

const SOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

const NEW_CUSTOM_THEME = "PBA";

const getPlayerKey = (BcUUID, { lane, playerNumber, playerName }) =>
  `${BcUUID}:${lane}:${playerNumber}:${playerName}`;

// Function to update a value in the Map
const updatePlayers = (fromMap, { key, value }) => {
  // Create a new Map for immutability
  const updatedMap = new Map(fromMap);
  updatedMap.set(key, value);
  return updatedMap;
};

const processPlayers = (players, UUID) => {
  // create a new Map object
  const playersMap = new Map();

  // return a key value pair of bowlingcenterUUID:LANE:PlayerNumber:PlayerName
  players.forEach((player) => {
    const key = getPlayerKey(UUID, player);
    playersMap.set(key, player);
  });
  return playersMap;
};

const isGameFinished = (throws) => {
  if (throws.length === 21) {
    return true;
  }

  if (throws.length === 20) {
    return !(throws[18] === "X" || throws[19] === "X" || throws[19] === "/");
  }

  return false;
};

const PublicLiveScorePlayers = () => {
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [players, setPlayers] = useState(new Map());

  const bcLanes = searchParams.get("lane").split("-").slice(0, 4) || [];
  const numberOfPlayers = +searchParams.get("noOfPlayers");
  const themeId = searchParams.get("theme");
  const compacted = searchParams.get("compacted") === "true";
  const xPos = +searchParams.get("x") || 0;
  const yPos = +searchParams.get("y") || 0;

  const {
    data: globalTheme,
    isLoading,
    isError,
  } = useQuery({
    enabled: themeId !== NEW_CUSTOM_THEME,
    queryKey: [queryKeys.themes.get],
    queryFn: () => getTheme(themeId),
    refetchOnWindowFocus: false,
    select: ({ data }) => data,
  });
  const { sendMessage, lastMessage, readyState } = useWebSocket(SOCKET_URL, {
    onOpen: () => console.log("opened"),
    onClose: () => console.log("closed"),
    //Will attempt to reconnect on all close events, such as server shutting down
    shouldReconnect: () => true,
  });

  const connectWebSocket = useCallback(() => {
    sendMessage(
      JSON.stringify({
        id: uuidv4(),
        method: 0,
        params: {
          api_key: API_KEY,
        },
      }),
    );
    sendMessage(
      JSON.stringify({
        id: uuidv4(),
        method: 1,
        params: {
          channel: `LiveScores:${searchParams.get("UUID")}`,
        },
      }),
    );
  }, []);

  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      connectWebSocket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyState]);

  // side effect for everytime we get a message
  useEffect(() => {
    if (lastMessage) {
      const message = JSON.parse(lastMessage.data);
      const bcUUID = searchParams.get("UUID");
      // process message the players that are only playing in the lane we're looking for
      // this is send just once when the websocket is connected
      if (message.result?.publications) {
        const playersInLane = message.result.publications
          ?.map(({ data }) =>
            data.lanes?.filter(({ lane }) => bcLanes.includes(lane.toString())),
          )
          .flat();

        const processedPlayers = processPlayers(playersInLane, bcUUID);
        setPlayers(processedPlayers);
      }

      // if message type === 5, then we have a new scores
      if (message.result?.type === 5) {
        const newPlayerData = message.result.data;
        // as the WS always returns scores from all lanes, we need to check if the player
        // is playing in the lane we're looking for.
        if (!bcLanes.includes(newPlayerData.lane.toString())) return;

        const key = getPlayerKey(bcUUID, newPlayerData);
        if (players.has(key)) {
          const updatedPlayers = updatePlayers(players, {
            key,
            value: newPlayerData,
          });
          setPlayers(updatedPlayers);
        } else {
          // filter out all players which game is finished from the players Map
          const currentlyPlaying = new Map(
            Array.from(players).filter(
              ([, player]) => !isGameFinished(player.throws),
            ),
          );
          // check the number of players in the Map, if it's equal to the number of players required
          // then we don't add the new player
          if (currentlyPlaying.size === numberOfPlayers * bcLanes.length)
            return;

          const updatedPlayers = updatePlayers(currentlyPlaying, {
            key,
            value: newPlayerData,
          });
          setPlayers(updatedPlayers);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  // update these for better UI
  if (isLoading && themeId !== NEW_CUSTOM_THEME)
    return (
      <div className={`${styles.container} ${styles.centerView}`}>
        <h3 className={styles.emptyMessage}>Loading...</h3>
      </div>
    );

  const themeBGColor =
    themeId === NEW_CUSTOM_THEME
      ? "#283337"
      : globalTheme?.livescores_settings?.background?.color;
  const background =
    globalTheme?.livescores_settings.background.type === "image"
      ? `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url("${globalTheme.livescores_settings.background.image}") no-repeat center / cover`
      : themeBGColor;

  // render Empty view
  if (players.size === 0)
    return (
      <div
        className={`${styles.container} ${styles.centerView}`}
        style={{
          background,
        }}
      >
        <h3 className={styles.emptyMessage}>
          {bcLanes.length > 1 ? "These lanes have" : "This lane has"} no players
          yet.
        </h3>
      </div>
    );

  // render PBA theme
  if (themeId === NEW_CUSTOM_THEME) {
    return (
      <div
        className={styles.pba_score}
        style={{ paddingTop: yPos, paddingLeft: xPos }}
      >
        {compacted ? (
          <PBAScoreCompacted
            players={players}
            numberOfPlayers={numberOfPlayers}
          />
        ) : (
          <PBAScoreFull players={players} numberOfPlayers={numberOfPlayers} />
        )}
      </div>
    );
  }

  if (isError || globalTheme.error) {
    const message = "The provided theme is not valid.";
    /* const message =
      isError.message ||
      (globalTheme.error === "record not found"
        ? "The provided theme is not valid."
        : globalTheme.error); */
    return (
      <div className={`${styles.container} ${styles.centerView}`}>
        <p
          style={{
            color: "red",
          }}
        >
          ERROR
        </p>
        <h3 className={styles.emptyMessage}>{message}</h3>
      </div>
    );
  }

  return (
    <div
      className={styles.container}
      style={{
        background,
      }}
    >
      <div
        className={styles.wrapper}
        style={{ paddingTop: yPos, paddingLeft: xPos }}
      >
        {globalTheme.livescores_settings.ten_frames_scorecard && (
          <div className={styles.lanesContainer}>
            {bcLanes.map((lane) => {
              const playersInLane = Array.from(players)
                .filter(([_, player]) => player.lane === +lane)
                .slice(0, numberOfPlayers);

              return playersInLane.length > 0 ? (
                <div
                  key={lane}
                  className={styles.cardsContainer}
                  style={{
                    backgroundColor:
                      globalTheme.scorecard_settings.lane_background,
                  }}
                >
                  {playersInLane.map(([key, player]) => (
                    <CardLaneV2
                      key={key}
                      player={player}
                      theme={globalTheme.scorecard_settings}
                    />
                  ))}
                </div>
              ) : (
                <div key={lane} style={{ width: 480 }} />
              );
            })}
          </div>
        )}
        {globalTheme.livescores_settings.compact_scorecard && (
          <div>
            <CardCompacted
              players={players}
              theme={globalTheme.scorecard_settings}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicLiveScorePlayers;
